/** Côté le plus long du tampon gardé, en pixels : net à l'impression, léger. */
export const COTE_TAMPON = 600;

/** Au-dessus de cette clarté (0-255), un pixel est le blanc du papier. */
const BLANC = 235;
/** En dessous, l'encre est gardée entière ; entre les deux, elle s'estompe. */
const ENCRE = 190;

/**
 * Rend le fond du tampon transparent et le recadre sur l'encre.
 *
 * <p>Un tampon scanné ou photographié arrive sur du papier blanc : posé sur
 * une facture, il en cachait le texte d'un rectangle blanc. Le blanc devient
 * transparent, les bords de l'encre s'estompent sans cassure, puis l'image est
 * recadrée au plus près de l'encre.
 *
 * @param {{data: Uint8ClampedArray, width: number, height: number}} image pixels RGBA, modifiés sur place
 * @returns {{x: number, y: number, largeur: number, hauteur: number}|null}
 *   le cadre de l'encre, ou null si l'image est vide
 */
export function detoure(image) {
  const { data, width, height } = image;
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const clarte = Math.min(data[i], data[i + 1], data[i + 2]);
      let alpha = data[i + 3];
      if (clarte >= BLANC) alpha = 0;
      else if (clarte > ENCRE) alpha = Math.round((alpha * (BLANC - clarte)) / (BLANC - ENCRE));
      data[i + 3] = alpha;
      if (alpha > 24) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  return { x: x0, y: y0, largeur: x1 - x0 + 1, hauteur: y1 - y0 + 1 };
}

/** Une toile de la taille voulue. */
function toile(largeur, hauteur) {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(largeur));
  c.height = Math.max(1, Math.round(hauteur));
  return c;
}

/** La première page d'un PDF, dessinée sur une toile. */
export async function premierePage(fichier) {
  const [pdfjs, { default: Travailleur }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerPort = new Travailleur();
  }
  const tache = pdfjs.getDocument({
    data: new Uint8Array(await fichier.arrayBuffer()),
    isEvalSupported: false,
  });
  try {
    const doc = await tache.promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const vue = page.getViewport({ scale: Math.min(4, 1800 / Math.max(base.width, base.height)) });
    const c = toile(vue.width, vue.height);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vue }).promise;
    return c;
  } finally {
    tache.destroy().catch(() => {});
  }
}

/** Une image (fichier ou adresse), dessinée sur une toile. */
export async function image(source) {
  const adresse = typeof source === 'string' ? source : URL.createObjectURL(source);
  try {
    const img = await new Promise((ok, ko) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => ko(new Error('Image illisible'));
      i.src = adresse;
    });
    const c = toile(img.naturalWidth || img.width, img.naturalHeight || img.height);
    c.getContext('2d').drawImage(img, 0, 0);
    return c;
  } finally {
    if (typeof source !== 'string') URL.revokeObjectURL(adresse);
  }
}

/**
 * Prépare le tampon de la société depuis un PDF, une image ou une photo.
 *
 * @param {File|Blob|string} source le fichier choisi, ou l'image d'une photo
 * @returns {Promise<string>} le tampon en PNG transparent (adresse data:)
 * @throws {Error} si le fichier ne contient pas d'encre lisible
 */
export async function prepareTampon(source) {
  const estPdf =
    typeof source !== 'string' &&
    (source.type === 'application/pdf' || /\.pdf$/i.test(source.name || ''));
  const c = estPdf ? await premierePage(source) : await image(source);
  const ctx = c.getContext('2d');
  const pixels = ctx.getImageData(0, 0, c.width, c.height);
  const cadre = detoure(pixels);
  if (!cadre) throw new Error('Aucun tampon trouvé dans ce fichier.');
  ctx.putImageData(pixels, 0, 0);
  const echelle = Math.min(1, COTE_TAMPON / Math.max(cadre.largeur, cadre.hauteur));
  const sortie = toile(cadre.largeur * echelle, cadre.hauteur * echelle);
  sortie
    .getContext('2d')
    .drawImage(
      c,
      cadre.x,
      cadre.y,
      cadre.largeur,
      cadre.hauteur,
      0,
      0,
      sortie.width,
      sortie.height,
    );
  return sortie.toDataURL('image/png');
}

/**
 * Pièces sur lesquelles le tampon et la signature du patron peuvent se poser,
 * dans l'ordre de Réglages → Documents. Les clés sont celles que le serveur
 * lit dans `soc.tamponSur` (SignatureEntreprise).
 */
export const SORTES_TAMPON = [
  ['devis', 'Offres et devis'],
  ['facture', 'Factures'],
  ['courrier', 'Courriers'],
  ['decharge', 'Décharges'],
  ['certificatSalaire', 'Certificats de salaire'],
  ['ficheSalaire', 'Fiches de salaire'],
  ['certificatTravail', 'Certificats de travail'],
  ['contrat', 'Contrats de travail'],
  ['licenciement', 'Lettres de licenciement'],
];

/**
 * Le patron veut-il son tampon sur cette sorte de pièce ? Une sorte qu'il n'a
 * jamais décochée le porte : il a importé son tampon pour qu'il paraisse.
 *
 * @param {object} soc réglages de la société
 * @param {string} sorte clé de SORTES_TAMPON
 * @returns {boolean} vrai sauf si la sorte est décochée
 */
export function tamponVoulu(soc, sorte) {
  return soc?.tamponSur?.[sorte] !== false;
}

/** Le tampon demandé sans tampon ni signature réglés : où les ajouter. */
export const MSG_SANS_TAMPON =
  'Aucun tampon ni signature enregistré : ajoute-les dans Réglages → Documents.';
